import { Link, useParams } from '@tanstack/react-router';
import { Building2, Check, ExternalLink, PhoneCall, Undo2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { BrandApplication } from '@nucleus-commerce/api-client/platform';
import { useFormat } from '@nucleus-commerce/i18n/react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';

import { canManageApplications } from '@/shared/session/roles';
import { session } from '@/shared/session/session';
import { DetailList, DetailRow } from '@/shared/ui/detail-list';
import { Mono } from '@/shared/ui/mono';

import { ApplicationNotesCard } from '../components/application-notes-card';
import { ApplicationStatusBadge } from '../components/application-status-badge';
import { ApproveApplicationDialog } from '../components/approve-application-dialog';
import { RejectApplicationDialog } from '../components/reject-application-dialog';
import { useApplication } from '../hooks/use-application';
import { useUpdateApplicationStatus } from '../hooks/use-update-application-status';
import { canDecide, canMarkContacted, canWithdraw } from '../schemas/application-rules';

const ROUTE_ID = '/_authed/applications/$applicationId' as const;

type Dialog = 'approve' | 'reject' | 'withdraw' | null;

/** Only http(s) addresses become links; the backend stores nothing else, this keeps it that way. */
function safeHref(website: string): string | null {
  try {
    const url = new URL(website);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

interface ActionsProps {
  application: BrandApplication;
  onOpen: (dialog: Exclude<Dialog, null>) => void;
}

/** The transitions available from the current status; only owners and admins see them. */
function ApplicationActions({ application, onOpen }: ActionsProps) {
  const { t } = useTranslation('applications');
  const update = useUpdateApplicationStatus(application.id);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {canMarkContacted(application) ? (
        <Button
          variant="outline"
          data-tour="platform.applications.mark-contacted"
          disabled={update.isPending}
          onClick={() => update.mutate('contacted')}
        >
          <PhoneCall aria-hidden />
          {t('actions.markContacted')}
        </Button>
      ) : null}
      {canWithdraw(application) ? (
        <Button variant="outline" onClick={() => onOpen('withdraw')}>
          <Undo2 aria-hidden />
          {t('actions.withdraw')}
        </Button>
      ) : null}
      {canDecide(application) ? (
        <>
          <Button
            variant="outline"
            className="text-destructive hover:text-destructive"
            data-tour="platform.applications.reject"
            onClick={() => onOpen('reject')}
          >
            <X aria-hidden />
            {t('actions.reject')}
          </Button>
          <Button data-tour="platform.applications.approve" onClick={() => onOpen('approve')}>
            <Check aria-hidden />
            {t('actions.approve')}
          </Button>
        </>
      ) : null}
    </div>
  );
}

function WithdrawDialog({
  application,
  open,
  onOpenChange,
}: {
  application: BrandApplication;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation(['applications', 'common']);
  const update = useUpdateApplicationStatus(application.id);
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('applications:withdraw.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('applications:withdraw.description', { brand: application.brandName })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={update.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={update.isPending}
            onClick={(event) => {
              event.preventDefault();
              update.mutate('withdrawn', { onSuccess: () => onOpenChange(false) });
            }}
          >
            {t('applications:withdraw.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ApplicationDetail() {
  const { t } = useTranslation('applications');
  const { applicationId } = useParams({ from: ROUTE_ID });
  const format = useFormat();
  const { me } = session.useSession();
  const canManage = canManageApplications(me?.user.role);
  const [dialog, setDialog] = useState<Dialog>(null);
  const { data: application } = useApplication(applicationId);

  if (!application) return null;

  const website = application.website ? safeHref(application.website) : null;
  const none = <span className="text-muted-foreground">{t('detail.none')}</span>;
  const date = (value: string | null) =>
    value ? <span title={format.utc(value)}>{format.dateTime(value)}</span> : none;
  const setOpen = (name: Exclude<Dialog, null>) => (open: boolean) => setDialog(open ? name : null);

  return (
    <PageContent>
      <PageHeader
        title={
          <span className="font-serif text-[1.875rem] font-normal">{application.brandName}</span>
        }
        meta={<ApplicationStatusBadge status={application.status} />}
        description={
          <span title={format.utc(application.createdAt)}>
            {t('detail.receivedAt', { date: format.dateTime(application.createdAt) })}
          </span>
        }
        backTo={{ to: '/applications' }}
        backLabel={t('title')}
        actions={
          canManage ? (
            <ApplicationActions application={application} onOpen={setDialog} />
          ) : undefined
        }
      />
      {application.organizationId ? (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm">{t('detail.provisioned')}</p>
            <Button asChild variant="outline">
              <Link
                to="/organizations/$organizationId"
                params={{ organizationId: application.organizationId }}
              >
                <Building2 aria-hidden />
                {t('approve.openOrganization')}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2" data-tour="platform.applications.brand">
          <CardHeader>
            <CardTitle>{t('detail.brand')}</CardTitle>
            <CardDescription>
              {t('detail.updatedAt', { date: format.dateTime(application.updatedAt) })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DetailList>
              <DetailRow label={t('detail.desiredSlug')}>
                {application.desiredSlug ? (
                  <Mono className="text-foreground">{application.desiredSlug}</Mono>
                ) : (
                  none
                )}
              </DetailRow>
              <DetailRow label={t('detail.website')}>
                {website ? (
                  <a
                    href={website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:underline"
                  >
                    {application.website}
                    <ExternalLink className="size-3.5" aria-hidden />
                  </a>
                ) : (
                  (application.website ?? none)
                )}
              </DetailRow>
              <DetailRow label={t('detail.country')}>{application.countryCode}</DetailRow>
              <DetailRow label={t('detail.currency')}>{application.currency ?? none}</DetailRow>
              <DetailRow label={t('detail.size')}>
                {t(`bands.${application.productCountBand}`)}
              </DetailRow>
              <DetailRow label={t('detail.source')}>{application.source ?? none}</DetailRow>
            </DetailList>
            <div className="mt-4 flex flex-col gap-1.5 text-sm">
              <span className="text-muted-foreground">{t('detail.message')}</span>
              {application.message ? (
                <p className="bg-muted/50 rounded-md p-3 whitespace-pre-wrap">
                  {application.message}
                </p>
              ) : (
                none
              )}
            </div>
          </CardContent>
        </Card>
        <Card data-tour="platform.applications.owner">
          <CardHeader>
            <CardTitle>{t('detail.owner')}</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailList>
              <DetailRow label={t('detail.ownerName')}>{application.ownerName}</DetailRow>
              <DetailRow label={t('detail.ownerEmail')}>
                <a href={`mailto:${application.ownerEmail}`} className="hover:underline">
                  {application.ownerEmail}
                </a>
              </DetailRow>
              <DetailRow label={t('detail.phone')}>
                {application.phone ? (
                  <a href={`tel:${application.phone}`} className="hover:underline">
                    {application.phone}
                  </a>
                ) : (
                  none
                )}
              </DetailRow>
              <DetailRow label={t('detail.emailVerifiedAt')}>
                {date(application.emailVerifiedAt)}
              </DetailRow>
            </DetailList>
          </CardContent>
        </Card>
        <ApplicationNotesCard
          className="lg:col-span-2"
          application={application}
          canWrite={canManage}
        />
        <Card>
          <CardHeader>
            <CardTitle>{t('detail.review')}</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailList>
              <DetailRow label={t('detail.status')}>
                <ApplicationStatusBadge status={application.status} />
              </DetailRow>
              <DetailRow label={t('detail.reviewedBy')}>
                {application.reviewedBy
                  ? (application.reviewedBy.name ?? t('notes.deletedUser'))
                  : none}
              </DetailRow>
              <DetailRow label={t('detail.reviewedAt')}>{date(application.reviewedAt)}</DetailRow>
              <DetailRow label={t('detail.decisionReason')}>
                {application.decisionReason ?? none}
              </DetailRow>
              <DetailRow label={t('detail.ip')}>
                {application.ip ? <Mono className="text-foreground">{application.ip}</Mono> : none}
              </DetailRow>
              <DetailRow label={t('detail.userAgent')}>
                {application.userAgent ? (
                  <span className="text-muted-foreground block truncate text-xs">
                    {application.userAgent}
                  </span>
                ) : (
                  none
                )}
              </DetailRow>
            </DetailList>
          </CardContent>
        </Card>
      </div>
      {canManage ? (
        <>
          <ApproveApplicationDialog
            application={application}
            open={dialog === 'approve'}
            onOpenChange={setOpen('approve')}
          />
          <RejectApplicationDialog
            application={application}
            open={dialog === 'reject'}
            onOpenChange={setOpen('reject')}
          />
          <WithdrawDialog
            application={application}
            open={dialog === 'withdraw'}
            onOpenChange={setOpen('withdraw')}
          />
        </>
      ) : null}
    </PageContent>
  );
}

export function ApplicationDetailPage() {
  return (
    <FeatureErrorBoundary name="applications.detail">
      <ApplicationDetail />
    </FeatureErrorBoundary>
  );
}
